// src/components/layout/CustomerLayout.js
import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from '../../utils/axios';
import {
  FiShoppingCart,
  FiUser,
  FiLogOut,
  FiPackage,
  FiSearch,
  FiMenu,
  FiX,
  FiHome,
  FiFileText,
  FiPhone,
  FiMail,
  FiMapPin,
  FiFacebook,
  FiInstagram,
  FiTwitter,
  FiClock,
  FiCalendar
} from 'react-icons/fi';

const CustomerLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [categories, setCategories] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [settings, setSettings] = useState({}); // ✅ Settings

  useEffect(() => {
    fetchUser();
    fetchCategories();
    loadCartCount();
    loadSettings(); // ✅ Settings татах
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const fetchUser = async () => {
    try {
      const response = await axios.get('/api/auth/me');
      
      if (response.data.success) {
        const userData = response.data.data.user;

        console.log('👤 User data:', userData);
        console.log('👤 Customer type:', userData.customer_type);
        console.log('👤 Credit limit:', userData.credit_limit);
        
        // Only customers can access
        if (userData.role_name === 'admin' || userData.role_name === 'staff') {
          toast.info('Админ хэрэглэгч админ панел руу шилжүүлж байна...');
          navigate('/admin/dashboard', { replace: true });
          return;
        }
        
        setUser(userData);
      }
    } catch (error) {
      console.error('Auth check error:', error);
      toast.error('Нэвтрэх эрх шалгахад алдаа гарлаа');
      navigate('/login', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/api/categories');
      if (response.data.success) {
        setCategories(response.data.data.categories.filter(cat => cat.is_active));
      }
    } catch (error) {
      console.error('Fetch categories error:', error);
    }
  };

  const loadCartCount = () => {
    const cart = JSON.parse(localStorage.getItem('customerCart') || '[]');
    const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);
    setCartCount(totalItems);
  };

  // ✅ Settings татах
  const loadSettings = async () => {
    try {
      const { data } = await axios.get('/api/settings/public');
      if (data.success) {
        setSettings(data.data.settings);
      }
    } catch (error) {
      console.error('Load settings error:', error);
    }
  };

  // ✅ Debounce timer ref нэмэх (useEffect-үүдийн дээр)
const searchTimerRef = React.useRef(null);

// ✅ Автомат хайлт (300ms debounce)
const handleSearchChange = (value) => {
  setSearchQuery(value);
  
  // Clear previous timer
  if (searchTimerRef.current) {
    clearTimeout(searchTimerRef.current);
  }
  
  // Set new timer
  if (value.trim()) {
    searchTimerRef.current = setTimeout(() => {
      navigate(`/customer/products?search=${encodeURIComponent(value.trim())}`);
    }, 500); // 500ms delay
  } else if (value === '') {
    // Хоосон бол бүх бараа харуулах
    navigate('/customer/products');
  }
};

// ✅ Enter дарахад шууд хайх
    const handleSearch = (e) => {
      e.preventDefault();
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
      if (searchQuery.trim()) {
        navigate(`/customer/products?search=${encodeURIComponent(searchQuery.trim())}`);
      }
    };

    // ✅ Cleanup on unmount
    React.useEffect(() => {
      return () => {
        if (searchTimerRef.current) {
          clearTimeout(searchTimerRef.current);
        }
      };
    }, []);

  const handleLogout = async () => {
    try {
      await axios.post('/api/auth/logout');
      localStorage.removeItem('customerCart');
      toast.success('Амжилттай гарлаа');
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Гарахад алдаа гарлаа');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="spinner-lg mb-4"></div>
          <p className="text-gray-600">Уншиж байна...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="customer-layout">
      {/* ✅ FIXED HEADER */}
      <header className="customer-header" style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50 }}>
        {/* Top Bar */}
        <div className="top-bar">
          <div className="container-custom">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-4 text-gray-600">
                {settings.contact_phone && (
                  <a href={`tel:${settings.contact_phone}`} className="flex items-center gap-1 hover:text-blue-600">
                    <FiPhone size={14} />
                    <span className="hidden sm:inline">{settings.contact_phone}</span>
                  </a>
                )}
                {settings.contact_email && (
                  <a href={`mailto:${settings.contact_email}`} className="flex items-center gap-1 hover:text-blue-600">
                    <FiMail size={14} />
                    <span className="hidden md:inline">{settings.contact_email}</span>
                  </a>
                )}
              </div>
              <div className="flex items-center gap-3">
                {/* Social Links */}
                {settings.social_facebook && (
                  <a href={settings.social_facebook} target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-blue-600">
                    <FiFacebook size={16} />
                  </a>
                )}
                {settings.social_instagram && (
                  <a href={settings.social_instagram} target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-pink-600">
                    <FiInstagram size={16} />
                  </a>
                )}
                {settings.social_twitter && (
                  <a href={settings.social_twitter} target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-sky-600">
                    <FiTwitter size={16} />
                  </a>
                )}
                <span className="hidden sm:inline text-gray-600">Тавтай морил, {user.full_name}!</span>
                <span className={`badge ${user.customer_type === 'wholesale' ? 'badge-blue' : 'badge-green'} text-xs`}>
                  {user.customer_type === 'wholesale' ? '🏢 Бөөний' : '🛒 Жижиглэн'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Header */}
        <div className="main-header">
          <div className="container-custom">
            <div className="flex items-center justify-between gap-4">
              {/* Logo */}
              <Link to="/customer/products" className="logo">
                {settings.header_logo ? (
                <img 
                  src={`${process.env.REACT_APP_API_URL}${settings.header_logo}`}                  
                  alt="Logo"
                  style={{ height: '80px', objectFit: 'contain' }}
                />
              ) : (
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                    <FiPackage className="text-white" size={24} />
                  </div>
                )}
                <div className="ml-3 hidden md:block">
                  <h1 className="text-xl font-bold text-gray-900">
                    {settings.company_name || 'Бөөний систем'}
                  </h1>
                  <p className="text-xs text-gray-500">{settings.site_tagline || 'Онлайн дэлгүүр'}</p>
                </div>
              </Link>

              {/* Search Bar - Desktop */}
              <form onSubmit={handleSearch} className="hidden lg:flex flex-1 max-w-xl">
                <div className="relative w-full">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    placeholder="Бараа хайх..."
                    className="w-full px-4 py-2.5 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                </div>
              </form>

              {/* Actions */}
              <div className="flex items-center gap-3">
                {/* Search - Mobile */}
                <button className="lg:hidden p-2 hover:bg-gray-100 rounded-lg">
                  <FiSearch size={20} />
                </button>

                {/* Cart */}
                <Link
                  to="/customer/cart"
                  className="relative p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <FiShoppingCart size={24} />
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                      {cartCount}
                    </span>
                  )}
                </Link>

                {/* Profile Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowProfileMenu(!showProfileMenu)}
                    className="flex items-center gap-2 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-700 font-semibold text-sm">
                        {user.full_name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <span className="hidden md:inline text-sm font-medium text-gray-700">
                      {user.full_name.split(' ')[0]}
                    </span>
                  </button>

                  {showProfileMenu && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setShowProfileMenu(false)}
                      ></div>
                      <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20">
                        <div className="px-4 py-2 border-b border-gray-100">
                          <p className="font-semibold text-gray-900">{user.full_name}</p>
                          <p className="text-xs text-gray-500">{user.email}</p>
                        </div>
                        <Link
                          to="/customer/orders"
                          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => setShowProfileMenu(false)}
                        >
                          <FiFileText size={16} />
                          Миний захиалга
                        </Link>
                        <Link
                          to="/customer/profile"
                          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => setShowProfileMenu(false)}
                        >
                          <FiUser size={16} />
                          Хувийн мэдээлэл
                        </Link>
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 border-t border-gray-100"
                        >
                          <FiLogOut size={16} />
                          Гарах
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {/* Mobile Menu Toggle */}
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
                >
                  {mobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Category Navigation */}
        <nav className="category-nav">
        <div className="container-custom">
          <div className="hidden lg:flex items-center gap-6 py-3">
            <Link
              to="/customer/products"
              className={`nav-link ${location.pathname === '/customer/products' ? 'active' : ''}`}
            >
              <FiHome size={18} />
              <span>Бүх бараа</span>
            </Link>
          </div>
        </div>
      </nav>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="mobile-menu lg:hidden">
            <div className="container-custom py-4">
              {/* Search - Mobile */}
              <form onSubmit={handleSearch} className="mb-4">
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    placeholder="Бараа хайх..."
                    className="w-full px-4 py-2.5 pl-10 border border-gray-300 rounded-lg"
                  />
                  <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                </div>
              </form>

              {/* Mobile Navigation */}
              <div className="space-y-2">
                <Link
                  to="/customer/products"
                  className="mobile-nav-link"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <FiHome size={18} />
                  <span>Бүх бараа</span>
                </Link>
                {categories.map(category => (
                  <Link
                    key={category.id}
                    to={`/customer/products?category=${category.id}`}
                    className="mobile-nav-link"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {category.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </header>

      {/* ✅ Main Content - Fixed header-тай padding */}
      <main className="customer-main" style={{ 
        paddingTop: '220px',  // Top bar (40px) + Main header (80px) + Category nav (60px)
        minHeight: 'calc(100vh - 180px)'
      }}>
        <Outlet context={{ user, loadCartCount, settings }} />
      </main>

      {/* ✅ Footer - Сайжруулсан gradient style */}
      <footer className="customer-footer" style={{ 
        background: 'linear-gradient(to bottom right, #1f2937, #111827, #000000)',
        color: '#d1d5db'
      }}>
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 py-12">
            {/* Company Info */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                {settings.footer_logo ? (
                  <img 
                    src={`${process.env.REACT_APP_API_URL}${settings.footer_logo}`}
                    alt="Footer Logo"
                    style={{ height: '64px', objectFit: 'contain' }}
                  />
                ) : (
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                    <FiPackage className="text-white" size={20} />
                  </div>
                )}
                <div>
                  <h3 className="font-bold text-white">
                    {settings.company_name || 'Бөөний систем'}
                  </h3>
                  <p className="text-xs text-gray-400">{settings.site_tagline || 'Онлайн дэлгүүр'}</p>
                </div>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed">
                {settings.company_slogan || 'Монгол улсын хамгийн том бөөний худалдааны онлайн систем. Чанартай бараа, найдвартай үйлчилгээ.'}
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold text-white mb-4">Холбоосууд</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/customer/products" className="text-gray-400 hover:text-blue-400 transition-colors">
                    Бүтээгдэхүүн
                  </Link>
                </li>
                <li>
                  <Link to="/customer/orders" className="text-gray-400 hover:text-blue-400 transition-colors">
                    Миний захиалга
                  </Link>
                </li>
                <li>
                  <Link to="/customer/profile" className="text-gray-400 hover:text-blue-400 transition-colors">
                    Хувийн мэдээлэл
                  </Link>
                </li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="font-semibold text-white mb-4">Ажлын цаг</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2 text-gray-400">
                  <FiClock size={16} className="text-blue-400" />
                  <span>{settings.working_hours || '09:00 - 18:00'}</span>
                </li>
                <li className="flex items-center gap-2 text-gray-400">
                  <FiCalendar size={16} className="text-blue-400" />
                  <span>{settings.working_days || 'Даваа - Баасан'}</span>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-semibold text-white mb-4">Холбоо барих</h4>
              <ul className="space-y-3 text-sm">
                {settings.contact_phone && (
                  <li className="flex items-center gap-2">
                    <FiPhone size={16} className="text-blue-400" />
                    <a href={`tel:${settings.contact_phone}`} className="text-gray-400 hover:text-blue-400 transition-colors">
                      {settings.contact_phone}
                    </a>
                  </li>
                )}
                {settings.contact_email && (
                  <li className="flex items-center gap-2">
                    <FiMail size={16} className="text-blue-400" />
                    <a href={`mailto:${settings.contact_email}`} className="text-gray-400 hover:text-blue-400 transition-colors">
                      {settings.contact_email}
                    </a>
                  </li>
                )}
                {settings.contact_address && (
                  <li className="flex items-center gap-2">
                    <FiMapPin size={16} className="text-blue-400" />
                    <span className="text-gray-400">{settings.contact_address}</span>
                  </li>
                )}
              </ul>

              {/* Social Media */}
              <div className="flex items-center gap-3 mt-4">
                {settings.social_facebook && (
                  <a 
                    href={settings.social_facebook} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors"
                  >
                    <FiFacebook size={16} />
                  </a>
                )}
                {settings.social_instagram && (
                  <a 
                    href={settings.social_instagram} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center hover:bg-pink-600 transition-colors"
                  >
                    <FiInstagram size={16} />
                  </a>
                )}
                {settings.social_twitter && (
                  <a 
                    href={settings.social_twitter} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center hover:bg-sky-500 transition-colors"
                  >
                    <FiTwitter size={16} />
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Copyright */}
            {/* Copyright */}
              <div className="border-t border-gray-700 py-4">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-sm">
                  <p className="text-gray-400">
                    © 2025 {settings.company_name || 'Бөөний систем'}. Бүх эрх хуулиар хамгаалагдсан.
                  </p>
                  
                  <div className="developer-credit-inline">
                    <span className="text-gray-500">Wholesale System</span>
                    <span className="text-gray-600 mx-1">•</span>
                    <span className="text-gray-500">Developed by</span>
                    <span className="text-blue-400 font-semibold mx-1">NG</span>
                    <span className="text-gray-600 mx-1">•</span>
                    <a 
                      href="tel:+97695051944" 
                      className="text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      +976-9505-1944
                    </a>
                  </div>
                </div>
              </div>
        </div>
      </footer>
    </div>
  );
};

export default CustomerLayout;